<!-- eslint-disable no-use-before-define -->
<script setup>
import { useAutocomplete } from './useAutocomplete';
import {
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
} from '../popover';
import { CsEmptyState } from '../empty-state';
import { CsListbox, CsListboxItem } from '../listbox';
import { CsIcon } from '../icon';
import { CsInput } from '../input';

import { ref, useSlots, watch, computed, onMounted, nextTick } from 'vue';

/**
 * @typedef {'bottom-start'|'top'|'right'|'bottom'|'left'|'top-start'|'top-end'|'right-start'|'right-end'|'bottom-end'|'left-start'|'left-end'} TooltipPlacement
 */

/**
 * @typedef {object} AutocompleteProps
 * @property {object} [value={}] - The selected value object
 * @property {Array} [suggestions=[]] - Array of suggestion items
 * @property {string} [placeholder='Select'] - Placeholder text for the input
 * @property {string} [label=''] - Label text for the input
 * @property {string} [labelTooltip=''] - Tooltip text for the label
 * @property {TooltipPlacement} [labelTooltipPlacement='right'] - Placement of the label tooltip
 * @property {string} [helperText=''] - Help text displayed below the input
 * @property {boolean} [isRequired=false] - Whether the input is required
 * @property {boolean} [isSearchable=true] - Whether search functionality is enabled
 * @property {boolean} [isClearable=true] - Whether the input can be cleared
 * @property {boolean} [isDisabled=false] - Whether the input is disabled
 * @property {object} [defaultEmptyValue={ id: '', name: '' }] - Default empty value object
 * @property {import('../icon/icon-types').IconName} [prefixIcon='email'] - Icon name for prefix
 * @property {import('../icon/icon-types').IconName} [suffixIcon='select'] - Icon name for suffix
 * @property {boolean} [isTeleport=false] - Whether to teleport the popover content
 * @property {number} [maxHeightSuggestions=400] - Maximum height of the suggestions list
 * @property {string} [compareBy='id'] - Property to compare suggestions
 * @property {string} [labelBy='name'] - Property to display in the label
 * @property {string} [valueBy='id'] - Property to use as the value
 * @property {string[]} [searchBy=['name']] - Properties to search in
 * @property {boolean} [isManualFilter=false] - Whether to manually filter suggestions
 * @property {number} [debounceTime=300] - Debounce time for the search
 * @property {boolean} [isFollowTriggerWidth=false] - Whether to follow the trigger width
 * @property {boolean} [isReadonly=false] - Whether the trigger input is readonly
 * @property {string} [labelValue=''] - Label value to display in the input
 * @property {boolean} [isKeepOpenOnSelect=false] - Whether to keep the popover open after selecting a suggestion
 */

/**
 * @type {AutocompleteProps}
 */
const props = defineProps({
  value: {
    type: Object,
    default: () => ({}),
  },
  suggestions: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isSearchable: {
    type: Boolean,
    default: true,
  },
  defaultEmptyValue: {
    type: Object,
    default: () => ({
      id: '',
      name: '',
    }),
  },
  prefixIcon: {
    type: String,
    default: '',
  },
  suffixIcon: {
    type: String,
    default: 'select',
  },
  helperText: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  isClearable: {
    type: Boolean,
    default: true,
  },
  labelTooltip: {
    type: String,
    default: '',
  },
  labelTooltipPlacement: {
    type: String,
    default: 'right',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isTeleport: {
    type: Boolean,
    default: false,
  },
  maxHeightSuggestions: {
    type: Number,
    default: 300,
  },
  compareBy: {
    type: String,
    default: 'id',
  },
  labelBy: {
    type: String,
    default: 'name',
  },
  valueBy: {
    type: String,
    default: 'id',
  },
  searchBy: {
    type: Array,
    default: () => ['name'],
  },
  isManualFilter: {
    type: Boolean,
    default: false,
  },
  debounceTime: {
    type: Number,
    default: 300,
  },
  isFollowTriggerWidth: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  labelValue: {
    type: String,
    default: '',
  },
  isKeepOpenOnSelect: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(
  'input',
  'change',
  'focus',
  'blur',
  'clear',
  'open',
  'close',
  'typing'
);

const slots = useSlots();
function hasSlot(name) {
  return slots[name] !== undefined;
}

const isInside = ref(false);
const triggerInputNode = ref(null);
const customTriggerNode = ref(null);
const searchInputNode = ref(null);
const contentNode = ref(null);

const triggerWidth = ref(0);
const isShowTooltip = ref(false);

function forceBlur() {
  isInside.value = false;

  const triggerElement = hasSlot('trigger')
    ? customTriggerNode.value
    : triggerInputNode.value;

  triggerElement?.blur();
  searchInputNode.value?.blur();
}

function adjustScrollPosition() {
  const container = contentNode.value.$el;
  const selectedElement = document.querySelector('[data-hover="true"]');

  if (container && selectedElement) {
    const containerRect = container.getBoundingClientRect();
    const elementRect = selectedElement.getBoundingClientRect();

    if (elementRect.top < containerRect.top) {
      selectedElement.scrollIntoView({ block: 'center' });
    } else if (elementRect.bottom > containerRect.bottom) {
      selectedElement.scrollIntoView({ block: 'center' });
    }
  }
}

const autocomplete = useAutocomplete({
  autoFocus: true,
  suggestions: computed(() => props.suggestions),
  initialValue: props.value,
  compareBy: props.compareBy,
  searchBy: props.searchBy,
  labelBy: props.labelBy,
  valueBy: props.valueBy,
  isManualFilter: props.isManualFilter,
  debounceTime: props.debounceTime,
  isKeepOpenOnSelect: props.isKeepOpenOnSelect,
  defaultEmptyValue: {
    id: '',
    name: '',
  },
  onSelect: value => {
    // Only force blur if not keeping open on select or if no more suggestions
    if (
      !props.isKeepOpenOnSelect ||
      autocomplete.suggestions.value.length <= 1
    ) {
      forceBlur();
    }

    emit('input', value);
    emit('change', value);

    handleRecalculate();
  },
  onTyping: payload => emit('typing', payload),
  onClear: payload => emit('clear', payload),
  onFocus: () => emit('focus'),
  onBlur: () => emit('blur'),
  onOpen: () => {
    emit('open');

    if (props.isFollowTriggerWidth) {
      const triggerElement = hasSlot('trigger')
        ? customTriggerNode.value
        : triggerInputNode.value;
      triggerWidth.value =
        (triggerElement?.$el || triggerElement)?.clientWidth || 0;
    }

    if (props.isSearchable) {
      searchInputNode.value?.focus();
    }

    adjustScrollPosition();
  },
  onClose: () => emit('close'),
});

function getSuggestionLabel(suggestion) {
  return suggestion[props.labelBy] || JSON.stringify(suggestion);
}

function onBlur() {
  if (!isInside.value) {
    // Close the popover only if not keeping open on select or if no more suggestions
    if (
      !props.isKeepOpenOnSelect ||
      autocomplete.suggestions.value.length === 0
    ) {
      autocomplete.close();
    }
  } else {
    // Focus to the trigger (default input or custom trigger)
    const triggerElement = hasSlot('trigger')
      ? customTriggerNode.value
      : triggerInputNode.value;
    triggerElement?.focus();
  }
}

function onSearchInputBlur() {
  onBlur();
}

function onTriggerInputBlur() {
  if (props.isSearchable) return;
  onBlur();
}

function handleKeydown(event) {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    autocomplete.onArrowDown();

    adjustScrollPosition();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    autocomplete.onArrowUp();

    adjustScrollPosition();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    autocomplete.onEnter();
  }
}

function handleRecalculate() {
  nextTick(() => {
    setTimeout(() => {
      if (triggerInputNode.value) {
        triggerInputNode.value.recalculatePrefixAndSuffixWidth();

        if (!hasSlot('trigger')) {
          const inputElement = triggerInputNode.value.input;
          if (inputElement) {
            const isTextTruncated =
              inputElement.scrollWidth > inputElement.clientWidth;
            isShowTooltip.value = isTextTruncated;
          }
        }
      }
    }, 0);
  });
}

function handleClear() {
  autocomplete.clear();
  handleRecalculate();
}

watch(
  () => props.value,
  newValue => {
    autocomplete.syncSelectedValue(newValue);
    handleRecalculate();
  }
);

onMounted(() => {
  autocomplete.syncSelectedValue(props.value);
  handleRecalculate();
});
</script>

<template>
  <CsPopover
    :is-manual="true"
    :is-open="autocomplete.isOpen.value"
    :is-teleport="props.isTeleport"
    :is-adaptive-width="!props.isFollowTriggerWidth"
    class="flex w-full"
    @close="autocomplete.close()"
  >
    <CsPopoverTrigger class="flex-1 w-full">
      <div
        v-if="hasSlot('trigger')"
        ref="customTriggerNode"
        class="inline-flex w-full"
      >
        <slot
          name="trigger"
          :value="autocomplete.getLabel.value"
          :is-open="autocomplete.isOpen.value"
          :is-disabled="props.isDisabled"
          :on-focus="autocomplete.open"
          :on-blur="onTriggerInputBlur"
          :on-keydown="handleKeydown"
          :on-clear="handleClear"
        />
      </div>

      <CsInput
        v-else
        ref="triggerInputNode"
        v-cs-tooltip="
          isShowTooltip ? props.labelValue || autocomplete.getLabel.value : ''
        "
        :value="props.labelValue || autocomplete.getLabel.value"
        class="w-full"
        input-class-name="truncate"
        :label="props.label"
        :is-required="props.isRequired"
        :placeholder="props.placeholder"
        :help-text="props.helperText"
        :error="props.error"
        :is-disabled="props.isDisabled"
        :is-clearable="props.isClearable"
        :label-tooltip="props.labelTooltip"
        :label-tooltip-placement="props.labelTooltipPlacement"
        :is-focused="autocomplete.isOpen.value"
        :is-readonly="true"
        @clear="handleClear"
        @focus="autocomplete.open()"
        @blur="onTriggerInputBlur"
        @keydown="handleKeydown"
      >
        <template v-if="props.prefixIcon || $slots.prefix" #prefix>
          <CsIcon
            v-if="props.prefixIcon"
            :name="props.prefixIcon"
            class="mr-0.5"
          />
          <slot v-if="$slots.prefix" name="prefix" />
        </template>

        <template #suffix>
          <slot v-if="$slots.suffix" name="suffix" />
          <CsIcon :name="props.suffixIcon" />
        </template>
      </CsInput>
    </CsPopoverTrigger>

    <CsPopoverContent class="w-full z-[99999]">
      <CsPopoverCard>
        <CsPopoverCardContent
          ref="contentNode"
          :class="
            props.isFollowTriggerWidth ? 'min-w-full' : 'w-[300px] min-w-full'
          "
          :style="{
            width: props.isFollowTriggerWidth ? `${triggerWidth}px` : undefined,
          }"
          @mouseenter="isInside = true"
          @mouseleave="isInside = false"
          @click="onTriggerInputBlur"
        >
          <div v-if="props.isSearchable" class="px-2.5 pt-2.5">
            <CsInput
              ref="searchInputNode"
              v-model="autocomplete.search.value"
              placeholder="Search"
              @blur="onSearchInputBlur"
              @keydown="handleKeydown"
            />
          </div>

          <CsEmptyState
            v-if="autocomplete.hasNoSuggestions.value"
            size="sm"
            icon-type="no-data"
            heading="Nothing to display yet"
            content="There are no data here yet"
            class="py-8"
          />

          <CsEmptyState
            v-else-if="autocomplete.hasNoSearchResults.value"
            size="sm"
            icon-type="no-result-found"
            heading="No results found"
            content="Your search didn't return any results. Please check your keywords or filters and try again"
            class="py-8"
          />

          <CsListbox
            v-else
            class="overflow-y-auto"
            :style="{ maxHeight: `${props.maxHeightSuggestions}px` }"
          >
            <CsListboxItem
              v-for="(suggestion, index) in autocomplete.suggestions.value"
              :key="suggestion.id"
              :is-active="autocomplete.isSelected(suggestion)"
              :is-hovered="autocomplete.isHovered(index)"
              :is-show-checkmark="true"
              :data-index="index"
              content-class="w-full"
              @click="autocomplete.setSelectedValue(suggestion)"
              @mouseenter="autocomplete.setCursorPosition(index)"
            >
              <slot
                v-if="hasSlot('suggestion')"
                name="suggestion"
                :suggestion="suggestion"
              />

              <template v-else>
                {{ getSuggestionLabel(suggestion) }}
              </template>
            </CsListboxItem>
          </CsListbox>

          <slot name="footer" :close="forceBlur" :blur="onBlur" />
        </CsPopoverCardContent>
      </CsPopoverCard>
    </CsPopoverContent>
  </CsPopover>
</template>
